import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { 
  Box, 
  Flex, 
  HStack, 
  Button, 
  Text, 
  useDisclosure, 
  Container,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Fade,
  Image,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Badge,
  Tooltip,
  Collapse,
  
} from '@chakra-ui/react';
import { 
  FaUserAlt, 
  FaCog, 
  FaCreditCard, 
  FaQuestionCircle, 
  FaChevronDown,
  FaHome,
  FaInfoCircle,
  FaFireAlt,
  FaImage,
  FaVideo,
  FaRobot,
  FaTh,
  FaWallet,
  FaCoins,
  FaCopy,
  FaGem,
  FaMagic,
  FaSignOutAlt,
  
} from 'react-icons/fa';
import './App.css';

// Import pages
import Login from './pages/Login';
import Home from './pages/Home';
import ImagePage from './pages/Image';
import VideoGenerate from './pages/VideoGenerate';
import AIChat from './pages/AIChat';
import NFTMaker from './pages/NFTMaker';
import Dashboard from './pages/Dashboard';
import Gallery from './pages/Gallery';
import Settings from './pages/Settings';
import BuyCredits from './pages/BuyCredits';
import BuyXGC from './pages/BuyXGC';
import Profile from './pages/Profile';
import Help from './pages/Help';
import Tutorial from './pages/Tutorial';
import Promotional from './pages/Promotional';

// Import components
import BackgroundEffects from './components/BackgroundEffects';
import WalletButton from './components/WalletButton';
import WalletInfo from './components/WalletInfo';
import Footer from './components/Footer';
import BuyCreditsModal from './components/BuyCreditsModal';

// Import contexts
import { WalletProvider } from './contexts/WalletContext';
import useWallet, { WALLET_STATUS } from './store/useWallet';

// Import utils
import { COLORS, ROUTES } from './utils/constants';
import useAuth from './store/useAuth';
import useCredits from './store/useCredits';

// Protected route component - Updated to use wallet authentication
const ProtectedRoute = ({ children, requireTokens = false, requireAdmin = false }) => {
  const { isLoggedIn } = useAuth();
  const { isConnected, hasRequiredTokens, isAdmin } = useWallet();
  
  // Check traditional auth first
  if (!isLoggedIn) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  
  // Check wallet requirements if specified
  if (requireTokens && (!isConnected || !hasRequiredTokens)) {
    return <Navigate to={ROUTES.HOME} replace />;
  }
  
  if (requireAdmin && (!isConnected || !isAdmin)) {
    return <Navigate to={ROUTES.HOME} replace />;
  }
  
  return children;
};

// Hamburger menu icon component with animation
const MenuIcon = ({ isOpen, ...props }) => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%" position="relative" {...props}>
    <Box
      as="span"
      display="block"
      w="24px"
      h="2px"
      bg="currentColor"
      position="absolute"
      transition="transform 0.3s ease"
      transform={isOpen ? "rotate(45deg)" : "rotate(0) translateY(-6px)"}
    />
    <Box
      as="span"
      display="block"
      w="24px"
      h="2px"
      bg="currentColor"
      position="absolute"
      transition="opacity 0.3s ease"
      opacity={isOpen ? 0 : 1}
    />
    <Box
      as="span"
      display="block"
      w="24px"
      h="2px"
      bg="currentColor"
      position="absolute"
      transition="transform 0.3s ease"
      transform={isOpen ? "rotate(-45deg)" : "rotate(0) translateY(6px)"}
    />
  </Box>
);

// NavLink component for consistent styling
const NavLink = ({ to, children, isActive }) => (
  <Button
    as={Link}
    to={to}
    variant="ghost"
    fontWeight={isActive ? "bold" : "medium"}
    color={isActive ? COLORS.primary : "whiteAlpha.900"}
    border="none"
    borderRadius="md"
    _hover={{
      color: COLORS.primary,
      bg: "rgba(0, 255, 127, 0.1)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0, 255, 127, 0.2)"
    }}
    _active={{
      color: COLORS.primary,
      bg: "rgba(0, 255, 127, 0.15)"
    }}
    transition="all 0.3s"
    size="md"
    px={4}
    letterSpacing="0.02em"
    fontFamily="Montserrat"
    textShadow={isActive ? "0 0 8px rgba(0, 255, 127, 0.5)" : "none"}
  >
    {children}
  </Button>
);

// Wallet display component for navigation
const WalletNavDisplay = () => {
  const { status, address, xgcBalance, xrpBalance } = useWallet();
  const { isLoggedIn, user, logout } = useAuth();
  
  // Debug logging
  console.log('WalletNavDisplay Debug:', { 
    status, 
    address, 
    isLoggedIn, 
    userWallet: user?.walletAddress,
    userAddress: user?.walletAddress
  });
  
  const handleLogout = () => {
    logout();
  };
  
  // If user is logged in but wallet status is not connected, show user info instead
  if (isLoggedIn && user?.walletAddress) {
    const truncatedAddress = `${user.walletAddress.substring(0, 6)}...${user.walletAddress.substring(user.walletAddress.length - 4)}`;
    
    return (
      <HStack spacing={2}>
        <Box 
          px={3}
          py={1}
          bg="rgba(0, 0, 0, 0.3)"
          borderRadius="md"
          borderWidth="1px"
          borderColor="rgba(0, 255, 127, 0.3)"
          boxShadow="0 0 10px rgba(0, 255, 127, 0.15)"
        >
          <HStack spacing={2}>
            <Icon as={FaWallet} color={COLORS.primary} />
            <VStack spacing={0} align="start">
              <Text color="white" fontSize="xs" fontWeight="medium">
                {truncatedAddress}
              </Text>
              <Text color="green.300" fontSize="xs">
                Connected
              </Text>
            </VStack>
          </HStack>
        </Box>
        <Tooltip label="Logout" placement="bottom">
          <IconButton
            aria-label="Logout"
            icon={<FaSignOutAlt />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            color="red.400"
            _hover={{ 
              bg: "rgba(255, 0, 0, 0.1)",
              color: "red.300"
            }}
            onClick={handleLogout}
          />
        </Tooltip>
      </HStack>
    );
  }
  
  // Fallback to wallet button if not logged in
  if (status !== WALLET_STATUS.CONNECTED || !address) {
    return (
      <WalletButton 
        size="sm" 
        variant="outline" 
        colorScheme="blue"
        showBalance={false}
        showAdmin={false}
      />
    );
  }
  
  // Show full wallet info if wallet is connected
  const truncatedAddress = `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  
  return (
    <HStack spacing={2}>
      <Box 
        px={3}
        py={1}
        bg="rgba(0, 0, 0, 0.3)"
        borderRadius="md"
        borderWidth="1px"
        borderColor="rgba(0, 255, 127, 0.3)"
        boxShadow="0 0 10px rgba(0, 255, 127, 0.15)"
      >
        <HStack spacing={2}>
          <Icon as={FaWallet} color={COLORS.primary} />
          <VStack spacing={0} align="start">
            <Text color="white" fontSize="xs" fontWeight="medium">
              {truncatedAddress}
            </Text>
            <HStack spacing={1}>
              <Text color={xgcBalance > 0 ? "green.300" : "red.300"} fontSize="xs">
                {xgcBalance.toLocaleString()} XGC
              </Text>
              <Text color="gray.400" fontSize="xs">
                ({xrpBalance.toFixed(2)} XRP)
              </Text>
            </HStack>
          </VStack>
        </HStack>
      </Box>
      <Tooltip label="Logout" placement="bottom">
        <IconButton
          aria-label="Logout"
          icon={<FaSignOutAlt />}
          size="sm"
          variant="ghost"
          colorScheme="red"
          color="red.400"
          _hover={{ 
            bg: "rgba(255, 0, 0, 0.1)",
            color: "red.300"
          }}
          onClick={handleLogout}
        />
      </Tooltip>
    </HStack>
  );
};

function App() {
  const { isLoggedIn, user, initializeAuth, logout } = useAuth();
  const { balance, fetchBalance } = useCredits();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isBuyCreditsOpen, onOpen: onBuyCreditsOpen, onClose: onBuyCreditsClose } = useDisclosure();
  const { isOpen: isCreateSectionOpen, onToggle: onCreateSectionToggle } = useDisclosure();
  const [isMounted, setIsMounted] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  
  // Initialize authentication on app start
  useEffect(() => {
    const initApp = async () => {
      try {
        await initializeAuth();
      } catch (error) {
        console.warn('Failed to initialize auth:', error);
      } finally {
        setIsInitializing(false);
        setIsMounted(true);
      }
    };
    
    initApp();
  }, [initializeAuth]);

  // Fetch credits when user changes
  useEffect(() => {
    if (user && user.walletAddress && !isInitializing) {
      fetchBalance(user.walletAddress);
    }
  }, [user, fetchBalance, isInitializing]);
  
  // Check if we're on special pages
  const isLoginPage = location.pathname === ROUTES.LOGIN;
  
  // Hide navigation on login page only (legacy auth pages removed)
  const hideNavigation = isLoginPage;
  
  // Check if each nav link is active
  const isRouteActive = (routePath) => {
    if (routePath === ROUTES.HOME) {
      return location.pathname === ROUTES.HOME || location.pathname === '/';
    }
    return location.pathname === routePath;
  };
  
  return (
    <WalletProvider>
      <Box 
        minH="100vh" 
        bg="#000000"
        position="relative"
        overflowX="hidden"
      >
        {/* Dynamic background effects */}
        <BackgroundEffects />
        
        {/* Navigation Bar - Hidden on login and promotional pages */}
        {!hideNavigation && (
          <Fade in={isMounted} transition={{ enter: { duration: 0.5 } }}>
            <Box 
              as="nav" 
              position="sticky" 
              top="0" 
              zIndex="20"
              bg="rgba(0, 0, 0, 0.98)"
              backdropFilter="blur(12px)" 
              borderBottom="1px solid rgba(0, 255, 127, 0.15)"
              boxShadow="0 4px 20px rgba(0, 0, 0, 0.6), 0 0 10px rgba(0, 255, 127, 0.1)"
            >
              <Container maxW="container.xl">
                <Flex py={3} alignItems="center" justifyContent="space-between">
                  {/* Logo */}
                  <Link to={ROUTES.HOME}>
                    <Flex alignItems="center">
                      <Image 
                        src="/IgnitaLogo.svg" 
                        alt="Ignita Logo" 
                        height="40px"
                      />
                      <Badge
                        ml={2}
                        bg="rgba(30, 220, 11, 0.2)"
                        color={COLORS.primary}
                        borderRadius="full"
                        px={2}
                        py={0.5}
                        fontSize="2xs"
                        fontWeight="bold"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        Beta
                      </Badge>
                    </Flex>
                  </Link>
                  
                  {/* Desktop Navigation */}
                  <HStack spacing={1} display={{ base: "none", md: "flex" }}>
                    {isLoggedIn ? (
                      <>
                        {/* Create Dropdown Menu */}
                        <Menu>
                          <MenuButton
                            as={Button}
                            variant="ghost"
                            fontWeight="medium"
                            color="whiteAlpha.900"
                            border="none"
                            borderRadius="md"
                            _hover={{
                              color: COLORS.primary,
                              bg: "rgba(0, 255, 127, 0.1)",
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 12px rgba(0, 255, 127, 0.2)"
                            }}
                            _active={{
                              color: COLORS.primary,
                              bg: "rgba(0, 255, 127, 0.15)"
                            }}
                            transition="all 0.3s"
                            size="md"
                            px={4}
                            letterSpacing="0.02em"
                            fontFamily="Montserrat"
                            rightIcon={<FaChevronDown />}
                          >
                            <Box className="nav-item">Create</Box>
                          </MenuButton>
                          <MenuList
                            bg={COLORS.background}
                            borderColor="rgba(30, 220, 11, 0.3)"
                            borderWidth="1px"
                            boxShadow="0 8px 25px rgba(0, 0, 0, 0.3), 0 0 15px rgba(30, 220, 11, 0.2)"
                            py={1}
                          >
                            <MenuItem
                              as={Link}
                              to={ROUTES.GENERATE}
                              bg="transparent"
                              color={COLORS.text}
                              _hover={{ 
                                bg: "rgba(30, 220, 11, 0.1)",
                                color: COLORS.primary
                              }}
                              _focus={{ 
                                bg: "rgba(30, 220, 11, 0.1)",
                                color: COLORS.primary
                              }}
                              icon={<Icon as={FaImage} color="currentColor" />}
                              fontWeight="medium"
                              py={3}
                            >
                              Image
                            </MenuItem>
                            <MenuItem
                              as={Link}
                              to={ROUTES.VIDEO}
                              bg="transparent"
                              color={COLORS.text}
                              _hover={{ 
                                bg: "rgba(30, 220, 11, 0.1)",
                                color: COLORS.primary
                              }}
                              _focus={{ 
                                bg: "rgba(30, 220, 11, 0.1)",
                                color: COLORS.primary
                              }}
                              icon={<Icon as={FaVideo} color="currentColor" />}
                              fontWeight="medium"
                              py={3}
                            >
                              Video
                            </MenuItem>
                            <MenuItem
                              as={Link}
                              to={ROUTES.NFT_MAKER}
                              bg="transparent"
                              color={COLORS.text}
                              _hover={{ 
                                bg: "rgba(30, 220, 11, 0.1)",
                                color: COLORS.primary
                              }}
                              _focus={{ 
                                bg: "rgba(30, 220, 11, 0.1)",
                                color: COLORS.primary
                              }}
                              icon={<Icon as={FaGem} color="currentColor" />}
                              fontWeight="medium"
                              py={3}
                            >
                              <HStack spacing={2} w="100%" justify="flex-start">
                                <Text color="inherit">NFT Maker</Text>
                                <Badge
                                  size="sm"
                                  bg="rgba(30, 220, 11, 0.2)"
                                  color={COLORS.primary}
                                  borderRadius="full"
                                  px={2}
                                  py={0.5}
                                  fontSize="2xs"
                                  fontWeight="bold"
                                >
                                  Soon
                                </Badge>
                              </HStack>
                            </MenuItem>
                          </MenuList>
                        </Menu>
                        
                        <NavLink to={ROUTES.CHAT} isActive={isRouteActive(ROUTES.CHAT)}>
                          <Box className="nav-item">Chat</Box>
                        </NavLink>
                        
                        <NavLink to={ROUTES.GALLERY} isActive={isRouteActive(ROUTES.GALLERY)}>
                          <Box className="nav-item">Gallery</Box>
                        </NavLink>
                        <NavLink to={ROUTES.TUTORIAL} isActive={isRouteActive(ROUTES.TUTORIAL)}>
                          <Box className="nav-item">How It Works</Box>
                        </NavLink>
                        
                        <Divider orientation="vertical" h="20px" mx={2} borderColor="whiteAlpha.400" />
                        
                        {/* Credits display with Buy button */}
                        <HStack spacing={2}>
                          <Box 
                            px={3}
                            py={1}
                            bg="rgba(0, 0, 0, 0.3)"
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor="rgba(0, 255, 127, 0.3)"
                            boxShadow="0 0 10px rgba(0, 255, 127, 0.15)"
                          >
                            <Text 
                              color="white" 
                              fontSize="sm" 
                              fontWeight="medium"
                            >
                              Credits: <Text as="span" color="white" fontWeight="bold">{balance}</Text>
                              {user?.isDevAccess && (
                                <Badge ml={2} bg="rgba(0, 168, 107, 0.2)" color="#ff5555" fontSize="2xs" verticalAlign="middle">
                                  DEV
                                </Badge>
                              )}
                            </Text>
                          </Box>
                          
                          <Tooltip label="Buy Credits">
                            <Button
                              size="sm"
                              onClick={onBuyCreditsOpen}
                              bg="rgba(0, 0, 0, 0.3)"
                              color="white"
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor="rgba(0, 255, 127, 0.3)"
                              boxShadow="0 0 10px rgba(0, 255, 127, 0.15)"
                              fontSize="sm"
                              fontWeight="medium"
                              px={3}
                              py={1}
                              _hover={{
                                color: COLORS.primary,
                                bg: "rgba(0, 255, 127, 0.1)",
                                borderColor: COLORS.accent,
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 12px rgba(0, 255, 127, 0.3)"
                              }}
                              _active={{
                                transform: "translateY(0)"
                              }}
                              transition="all 0.3s"
                            >
                              Buy Credits
                            </Button>
                          </Tooltip>
                        </HStack>
                        
                        {/* Wallet display */}
                        <WalletNavDisplay />
                        
                        {/* <Menu>
                          <MenuButton
                            as={Button}
                            variant="ghost"
                            color="white"
                            fontWeight="medium"
                            borderRadius="0"
                            rightIcon={<Icon as={FaChevronDown} boxSize="12px" />}
                            _hover={{
                              color: "white",
                              bg: "whiteAlpha.200",
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)"
                            }}
                            _active={{
                              color: "white",
                              bg: "whiteAlpha.300"
                            }}
                          >
                            Account
                          </MenuButton>
                          <MenuList 
                            bg={COLORS.background} 
                            borderColor={COLORS.primary}
                            borderWidth="1px"
                            borderRadius="md"
                            boxShadow="0 4px 20px rgba(0, 168, 107, 0.3)"
                            p={2}
                            mt={1}
                          >
                            <MenuItem 
                              as={Link} 
                              to={ROUTES.PROFILE}
                              color={COLORS.text}
                              bg={COLORS.background}
                              borderRadius="md"
                              _hover={{ bg: "rgba(30, 220, 11, 0.2)" }}
                              mb={1}
                            >
                              <Icon as={FaUserAlt} mr={2} />
                              Profile
                            </MenuItem>
                            <MenuItem 
                              as={Link} 
                              to={ROUTES.SETTINGS}
                              color={COLORS.text}
                              bg={COLORS.background}
                              borderRadius="md"
                              _hover={{ bg: "rgba(30, 220, 11, 0.2)" }}
                              mb={1}
                            >
                              <Icon as={FaCog} mr={2} />
                              Settings
                            </MenuItem>
                            <MenuItem 
                              onClick={onBuyCreditsOpen}
                              color={COLORS.text}
                              bg={COLORS.background}
                              borderRadius="md"
                              _hover={{ bg: "rgba(30, 220, 11, 0.2)" }}
                              mb={1}
                            >
                              <Icon as={FaCreditCard} mr={2} />
                              Buy Credits ({balance})
                            </MenuItem>
                            <MenuItem 
                              as={Link} 
                              to={ROUTES.BUY_XGC}
                              color={COLORS.text}
                              bg={COLORS.background}
                              borderRadius="md"
                              _hover={{ bg: "rgba(30, 220, 11, 0.2)" }}
                              mb={1}
                            >
                              <Icon as={FaCoins} mr={2} />
                              Buy XGC
                            </MenuItem>
                            <MenuItem 
                              as={Link} 
                              to={ROUTES.HELP}
                              color={COLORS.text}
                              bg={COLORS.background}
                              borderRadius="md"
                              _hover={{ bg: "rgba(30, 220, 11, 0.2)" }}
                            >
                              <Icon as={FaQuestionCircle} mr={2} />
                              Help & FAQ
                            </MenuItem>
                          </MenuList>
                        </Menu> */}
                      </>
                    ) : (
                      <>
                        <NavLink to={ROUTES.HOME} isActive={isRouteActive(ROUTES.HOME)}>
                          <Box className="nav-item">Home</Box>
                        </NavLink>
                        <NavLink to={ROUTES.TUTORIAL} isActive={isRouteActive(ROUTES.TUTORIAL)}>
                          <Box className="nav-item">How It Works</Box>
                        </NavLink>
                        <NavLink to={ROUTES.LOGIN} isActive={isRouteActive(ROUTES.LOGIN)}>
                          <Box className="nav-item">Login</Box>
                        </NavLink>
                      </>
                    )}
                  </HStack>
                  
                  {/* Mobile Menu Button */}
                  <IconButton
                    display={{ base: "flex", md: "none" }}
                    onClick={onOpen}
                    variant="ghost"
                    aria-label="Open menu"
                    color="white"
                    _hover={{ bg: "whiteAlpha.300" }}
                    icon={<MenuIcon isOpen={isOpen} />}
                  />
                </Flex>
              </Container>
            </Box>
          </Fade>
        )}
        
        {/* Mobile Navigation Drawer */}
        <Drawer placement="right" onClose={onClose} isOpen={isOpen}>
          <DrawerOverlay zIndex={20} />
          <DrawerContent 
          bg={COLORS.background} 
  
          boxShadow={`-5px 0 25px rgba(0, 0, 0, 0.5), 0 0 15px rgba(30, 220, 11, 0.2)`}
          zIndex={20}>
            <DrawerCloseButton color={COLORS.text} />
            <DrawerHeader 
              bg={COLORS.background}
              borderBottomWidth="1px"
              borderColor="rgba(30, 220, 11, 0.3)"
              py={4}
            >
              <Flex alignItems="center">
                <Image 
                  src="/IgnitaLogo.svg" 
                  alt="Ignita Logo" 
                  height="30px"
                  mr={3}
                />
                <Badge
                  bg="rgba(30, 220, 11, 0.2)"
                  color={COLORS.primary}
                  borderRadius="full"
                  px={2}
                  py={0.5}
                  fontSize="2xs"
                  fontWeight="bold"
                  textTransform="uppercase"
                  letterSpacing="0.5px"
                >
                  Beta
                </Badge>
              </Flex>
            </DrawerHeader>
            <DrawerBody p={0}>
              <VStack spacing={0} align="stretch">
                {isLoggedIn ? (
                  <>
                    {/* Wallet info in mobile menu */}
                    <Box p={4} borderBottom="1px solid rgba(30, 220, 11, 0.2)">
                      <WalletInfo compact showBalance={true} showAdmin={true} />
                    </Box>
                    <Button
  onClick={() => {
    onBuyCreditsOpen();
    onClose(); // Close mobile menu when opening modal
  }}
  bg="rgba(30, 220, 11, 0.1)"
  color={COLORS.primary}
  borderColor="rgba(30, 220, 11, 0.3)"
  borderWidth="1px"
  fontWeight="bold"
  fontSize="md"
  py={4}
  mx={4}
  my={2}
  borderRadius="md"
  leftIcon={<Icon as={FaCoins} />}
  _hover={{ 
    bg: "rgba(30, 220, 11, 0.2)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(30, 220, 11, 0.3)"
  }}
  _active={{ transform: "translateY(0)" }}
  transition="all 0.3s"
>
  Buy Credits
</Button>
                    
                    {/* Collapsible Create Section Header */}
                    <Button
                      variant="ghost"
                      justifyContent="space-between"
                      color={COLORS.primary}
                      fontWeight="bold"
                      fontSize="md"
                      p={4}
                      borderBottom="1px solid rgba(30, 220, 11, 0.2)"
                      bg="rgba(30, 220, 11, 0.05)"
                      borderRadius="0"
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onCreateSectionToggle}
                      rightIcon={
                        <Icon 
                          as={FaChevronDown} 
                          transform={isCreateSectionOpen ? "rotate(180deg)" : "rotate(0deg)"}
                          transition="transform 0.3s ease"
                        />
                      }
                    >
                      <HStack spacing={2}>
                        <Icon as={FaMagic} color={COLORS.primary} />
                        <Text color={COLORS.primary}>Create</Text>
                      </HStack>
                    </Button>
                    
                    <Collapse in={isCreateSectionOpen} animateOpacity>
                      <VStack spacing={0} align="stretch">
                        <Button
                          as={Link}
                          to={ROUTES.GENERATE}
                          variant="ghost"
                          justifyContent="flex-start"
                          color={COLORS.text}
                          fontWeight="medium"
                          py={6}
                          px={8}
                          borderRadius="0"
                          leftIcon={<Icon as={FaImage} />}
                          _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                          onClick={onClose}
                          bg="rgba(0, 0, 0, 0.2)"
                        >
                          Image
                        </Button>
                        <Button
                          as={Link}
                          to={ROUTES.VIDEO}
                          variant="ghost"
                          justifyContent="flex-start"
                          color={COLORS.text}
                          fontWeight="medium"
                          py={6}
                          px={8}
                          borderRadius="0"
                          leftIcon={<Icon as={FaVideo} />}
                          _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                          onClick={onClose}
                          bg="rgba(0, 0, 0, 0.2)"
                        >
                          Video
                        </Button>
                        
                        {/* NFT Maker - Coming Soon (Mobile) */}
                        <Button
                          as={Link}
                          to={ROUTES.NFT_MAKER}
                          variant="ghost"
                          justifyContent="flex-start"
                          color={COLORS.text}
                          fontWeight="medium"
                          py={6}
                          px={8}
                          borderRadius="0"
                          leftIcon={<Icon as={FaGem} />}
                          _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                          onClick={onClose}
                          bg="rgba(0, 0, 0, 0.2)"
                        >
                          <HStack spacing={2} w="100%" justify="flex-start">
                            <Text>NFT Maker</Text>
                            <Badge
                              size="sm"
                              bg="rgba(30, 220, 11, 0.2)"
                              color={COLORS.primary}
                              borderRadius="full"
                              px={2}
                              py={0.5}
                              fontSize="2xs"
                              fontWeight="bold"
                            >
                              Soon
                            </Badge>
                          </HStack>
                        </Button>
                      </VStack>
                    </Collapse>
                    
                    <Divider borderColor="rgba(30, 220, 11, 0.2)" />
                    
                    <Button
                      as={Link}
                      to={ROUTES.CHAT}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaRobot} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onClose}
                    >
                      Chat
                    </Button>
                    
                    <Button
                      as={Link}
                      to={ROUTES.GALLERY}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaImage} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onClose}
                    >
                      Gallery
                    </Button>

                    <Button
                      as={Link}
                      to={ROUTES.TUTORIAL}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaInfoCircle} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onClose}
                    >
                      How It Works
                    </Button>
                    
                    <Divider borderColor="rgba(30, 220, 11, 0.2)" />
                    
                    {/* Logout Button */}
                    <Button
                      variant="ghost"
                      justifyContent="flex-start"
                      color="red.400"
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaSignOutAlt} />}
                      _hover={{ 
                        bg: "rgba(255, 0, 0, 0.1)",
                        color: "red.300"
                      }}
                      onClick={() => {
                        logout();
                        onClose();
                      }}
                    >
                      Logout
                    </Button>
                    
                    {/* <Divider borderColor="rgba(30, 220, 11, 0.2)" />
                    
                    <Button
                      as={Link}
                      to={ROUTES.PROFILE}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaUserAlt} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onClose}
                    >
                      Profile
                    </Button>
                    <Button
                      as={Link}
                      to={ROUTES.SETTINGS}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaCog} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onClose}
                    >
                      Settings
                    </Button>
                    <Button
                      onClick={() => {
                        onClose();
                        onBuyCreditsOpen();
                      }}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaCreditCard} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                    >
                      Buy Credits ({balance})
                    </Button>
                    <Button
                      as={Link}
                      to={ROUTES.BUY_XGC}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaCoins} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onClose}
                    >
                      Buy XGC
                    </Button>
                    <Button
                      as={Link}
                      to={ROUTES.HELP}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaQuestionCircle} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onClose}
                    >
                      Help & FAQ
                    </Button> */}
                  </>
                ) : (
                  <>
                    <Button
                      as={Link}
                      to={ROUTES.HOME}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaHome} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onClose}
                    >
                      Home
                    </Button>
                    <Button
                      as={Link}
                      to={ROUTES.TUTORIAL}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaInfoCircle} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onClose}
                    >
                      How It Works
                    </Button>
                    <Button
                      as={Link}
                      to={ROUTES.LOGIN}
                      variant="ghost"
                      justifyContent="flex-start"
                      color={COLORS.text}
                      fontWeight="medium"
                      py={6}
                      px={6}
                      borderRadius="0"
                      leftIcon={<Icon as={FaUserAlt} />}
                      _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                      onClick={onClose}
                    >
                      Login
                    </Button>
                  </>
                )}
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
        
        {/* Main Content */}
        <Box as="main" position="relative" zIndex="1">
          <Routes>
            <Route path={ROUTES.LOGIN} element={<Login />} />
            {/* Legacy auth routes removed in wallet-only migration */}
            <Route path={ROUTES.HOME} element={<Home />} />
            <Route path={ROUTES.TUTORIAL} element={<Tutorial />} />
            <Route path={ROUTES.PROMOTIONAL} element={<Promotional />} />
            
            {/* Protected Routes */}
            <Route 
              path={ROUTES.GENERATE} 
              element={
                <ProtectedRoute>
                  <ImagePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.VIDEO} 
              element={
                <ProtectedRoute>
                  <VideoGenerate />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.CHAT} 
              element={
                <ProtectedRoute>
                  <AIChat />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.NFT_MAKER} 
              element={
                <ProtectedRoute>
                  <NFTMaker />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.DASHBOARD} 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.GALLERY} 
              element={
                <ProtectedRoute>
                  <Gallery />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.PROFILE} 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.SETTINGS} 
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.BUY_CREDITS} 
              element={
                <ProtectedRoute>
                  <BuyCredits />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.BUY_XGC} 
              element={
                <ProtectedRoute>
                  <BuyXGC />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.HELP} 
              element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              } 
            />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
          </Routes>
        </Box>
        
        {/* Footer */}
        {!hideNavigation && <Footer />}

        {/* Buy Credits Modal */}
        <BuyCreditsModal isOpen={isBuyCreditsOpen} onClose={onBuyCreditsClose} />
      </Box>
    </WalletProvider>
  );
}

export default App;
